import React from 'react';
import {StyleSheet, View} from 'react-native';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {useAuthStore} from '@/entities/session/auth-store';
import type {DevicesStackParamList} from '@/app/routes';
import {fetchDevice} from '@/entities/device/api';
import {isTerminalLike, managingDesktop, type DeviceDetail} from '@/entities/device/types';
import {DeviceUpdatePanel} from '@/features/update-device/ui/DeviceUpdatePanel';
import {ProfilePicker} from '@/features/manage-terminal-settings/ui/ProfilePicker';
import {TerminalSettingsForm} from '@/features/manage-terminal-settings/ui/TerminalSettingsForm';
import {TerminalControlPanel} from '@/features/control-terminal/ui/TerminalControlPanel';
import {CommandHistory} from '@/features/manage-device/ui/CommandHistory';
import {DeviceAdminPanel} from '@/features/manage-device/ui/DeviceAdminPanel';
import {LanKeyCard} from '@/features/manage-device/ui/LanKeyCard';
import {DeviceAccountsCard} from '@/features/manage-device-accounts/ui/DeviceAccountsCard';
import {ManagedByDesktopBanner} from '@/features/view-devices/ui/ManagedByDesktopBanner';
import {FactoryResetPanel} from '@/features/reset-device/ui/FactoryResetPanel';
import {EmptyState} from '@/shared/ui/empty-state';
import {Page} from '@/shared/ui/page';
import {Section} from '@/shared/ui/section';
import {Skeleton} from '@/shared/ui/skeleton';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * «Устройства» → карточка → настройки терминала.
 *
 * Порядок блоков — от того, что делают часто, к тому, что делают один раз:
 * управление → история команд → администрирование → доступ (LAN-ключ и учётки) →
 * профили и настройки → сброс. Сброс последний не для красоты: до него нужно
 * долистать.
 */
type Props = NativeStackScreenProps<DevicesStackParamList, 'DeviceSettings'>;

const styles = StyleSheet.create({
  loading: {gap: spacing(4)},
});

export function DeviceSettingsScreen({route, navigation}: Props) {
  const {t} = useTranslation();
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  const {deviceId, name} = route.params;

  const query = useQuery({
    queryKey: ['device', companyId, deviceId],
    queryFn: () => fetchDevice(companyId as string, deviceId),
    enabled: !!companyId,
  });

  let body: React.ReactNode;
  if (!companyId || query.isPending) {
    body = (
      <View style={styles.loading}>
        <Skeleton height={140} radius={radii.xl} />
        <Skeleton height={110} radius={radii.xl} />
        <Skeleton height={220} radius={radii.xl} />
      </View>
    );
  } else if (query.isError) {
    body = (
      <EmptyState
        tone="error"
        title={t('home.device.notFound')}
        actionLabel={t('actions.retry')}
        onAction={() => void query.refetch()}
      />
    );
  } else {
    const detail: DeviceDetail = query.data;
    // Behind a desktop (SV-13) everything that writes to the board — commands,
    // update, accounts, profile, settings, reset — is refused by the server with 409,
    // so it is not offered. What stays is what the cloud still owns: the history, the
    // row itself (name, department, delete) and the LAN key.
    const manager = managingDesktop(detail.summary);
    body = (
      <>
        {manager ? <ManagedByDesktopBanner manager={manager} /> : null}

        {!manager ? (
          <Section title={t('control.title')}>
            <TerminalControlPanel
              companyId={companyId}
              deviceId={deviceId}
              hal={detail.summary.hal_status}
            />
          </Section>
        ) : null}

        {!manager && isTerminalLike(detail.summary.device_type) ? (
          <Section title={t('update.title')} hint={t('update.subtitle')}>
            <DeviceUpdatePanel
              companyId={companyId}
              deviceId={deviceId}
              summary={detail.summary}
            />
          </Section>
        ) : null}

        <Section title={t('commands.history')} hint={t('commands.historyHint')}>
          <CommandHistory companyId={companyId} deviceId={deviceId} />
        </Section>

        <Section>
          <DeviceAdminPanel
            companyId={companyId}
            device={detail.summary}
            onDeleted={() => navigation.popTo('DeviceList')}
          />
        </Section>

        {/* LAN-ключ стоит рядом с учётками терминала, а не рядом с настройками: и то
            и другое — ответ на вопрос «кому можно разговаривать с этой платой», и ни
            то ни другое плата не применяет как настройку. */}
        <Section title={t('lanKey.title')} hint={t('lanKey.subtitle')}>
          <LanKeyCard companyId={companyId} deviceId={deviceId} />
        </Section>

        {!manager ? (
          <>
            <Section title={t('terminal.accounts.title')}>
              <DeviceAccountsCard
                companyId={companyId}
                deviceId={deviceId}
                summary={detail.summary}
              />
            </Section>

            <Section title={t('terminal.profiles')}>
              <ProfilePicker
                companyId={companyId}
                deviceId={deviceId}
                assignedProfileId={detail.assigned_profile_id}
              />
            </Section>

            <Section title={t('terminal.settings')}>
              <TerminalSettingsForm
                key={JSON.stringify(detail.effective_settings)}
                companyId={companyId}
                deviceId={deviceId}
                settings={detail.effective_settings}
              />
            </Section>

            <Section title={t('reset.title')}>
              <FactoryResetPanel
                companyId={companyId}
                deviceId={deviceId}
                deviceName={detail.summary.device_name}
                onFinalized={() => navigation.popTo('DeviceList')}
              />
            </Section>
          </>
        ) : null}
      </>
    );
  }

  return (
    <Page
      scroll
      width="form"
      title={name}
      subtitle={t('terminal.settings')}
      testID="device-settings">
      {body}
    </Page>
  );
}
